import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUser, faSearch, faCog, faKey, faBell, 
    faShieldAlt, faQuestionCircle, faEdit, faSave
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';
import Swal from 'sweetalert2';

interface AccountSettings {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    timezone: string;
    language: string;
    notifications: {
        email: boolean;
        sms: boolean;
        push: boolean;
    };
    security: {
        twoFactor: boolean;
        sessionTimeout: number;
    };
}

const AccountHelp: React.FC = () => {
    const [settings, setSettings] = useState<AccountSettings>({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        timezone: 'UTC',
        language: 'en',
        notifications: {
            email: true,
            sms: false,
            push: true
        },
        security: {
            twoFactor: false,
            sessionTimeout: 30
        }
    });
    const [loading, setLoading] = useState(false);
    const [clinicName, setClinicName] = useState<string>('Facility');

    useEffect(() => {
        document.title = "Account Help - Uzima Healthcare";
        loadClinicData();
        loadAccountSettings();
    }, []);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const loadAccountSettings = async () => {
        try {
            // Mock loading account settings - in real app, fetch from API
            const mockSettings: AccountSettings = {
                firstName: 'John',
                lastName: 'Doe',
                email: 'john.doe@clinic.com',
                phone: '+1 (555) 123-4567',
                timezone: 'America/New_York',
                language: 'en',
                notifications: {
                    email: true,
                    sms: false,
                    push: true
                },
                security: {
                    twoFactor: false,
                    sessionTimeout: 30
                }
            };
            setSettings(mockSettings);
        } catch (error) {
            console.error('Error loading account settings:', error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setSettings(prev => {
                const parentValue = prev[parent as keyof AccountSettings] as any;
                return {
                    ...prev,
                    [parent]: {
                        ...parentValue,
                        [child]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
                    }
                };
            });
        } else {
            setSettings(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
            }));
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1500));
            
            Swal.fire({
                icon: 'success',
                title: 'Settings Saved!',
                text: 'Your account settings have been updated successfully.',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error saving settings:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to save settings. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const changePassword = () => {
        Swal.fire({
            title: 'Change Password',
            html: `
                <div class="text-start">
                    <div class="mb-3">
                        <label class="form-label">Current Password</label>
                        <input type="password" class="form-control" id="currentPassword" placeholder="Enter current password">
                    </div>
                    <div class="mb-3">
                        <label class="form-label">New Password</label>
                        <input type="password" class="form-control" id="newPassword" placeholder="Enter new password">
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Confirm New Password</label>
                        <input type="password" class="form-control" id="confirmPassword" placeholder="Confirm new password">
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Change Password',
            cancelButtonText: 'Cancel',
            preConfirm: () => {
                const currentPassword = (document.getElementById('currentPassword') as HTMLInputElement).value;
                const newPassword = (document.getElementById('newPassword') as HTMLInputElement).value;
                const confirmPassword = (document.getElementById('confirmPassword') as HTMLInputElement).value;
                
                if (!currentPassword || !newPassword || !confirmPassword) {
                    Swal.showValidationMessage('Please fill in all fields');
                    return false;
                }
                
                if (newPassword !== confirmPassword) {
                    Swal.showValidationMessage('New passwords do not match');
                    return false;
                }
                
                if (newPassword.length < 8) {
                    Swal.showValidationMessage('Password must be at least 8 characters long');
                    return false;
                }
                
                return { currentPassword, newPassword };
            }
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    icon: 'success',
                    title: 'Password Changed!',
                    text: 'Your password has been updated successfully.'
                });
            }
        });
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <h2 className="fw-bold mb-1">
                                    <FontAwesomeIcon icon={faUser} className="me-2 text-primary" />
                                    Account Help
                                </h2>
                                <p className="text-muted mb-0">
                                    Manage your account settings and preferences for {clinicName}
                                </p>
                            </div>
                        </div>

                        <div className="row">
                            {/* Account Settings */}
                            <div className="col-lg-8">
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white border-0">
                                        <h5 className="card-title mb-0">
                                            <FontAwesomeIcon icon={faCog} className="me-2" />
                                            Account Settings
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <form>
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">First Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="firstName"
                                                        value={settings.firstName}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Last Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="lastName"
                                                        value={settings.lastName}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">Email Address</label>
                                                    <input
                                                        type="email"
                                                        className="form-control"
                                                        name="email"
                                                        value={settings.email}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Phone Number</label>
                                                    <input
                                                        type="tel"
                                                        className="form-control"
                                                        name="phone"
                                                        value={settings.phone}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">Timezone</label>
                                                    <select
                                                        className="form-select"
                                                        name="timezone"
                                                        value={settings.timezone}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="UTC">UTC</option>
                                                        <option value="America/New_York">Eastern Time</option>
                                                        <option value="America/Chicago">Central Time</option>
                                                        <option value="America/Denver">Mountain Time</option>
                                                        <option value="America/Los_Angeles">Pacific Time</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Language</label>
                                                    <select
                                                        className="form-select"
                                                        name="language"
                                                        value={settings.language}
                                                        onChange={handleInputChange}
                                                    >
                                                        <option value="en">English</option>
                                                        <option value="es">Spanish</option>
                                                        <option value="fr">French</option>
                                                        <option value="de">German</option>
                                                    </select>
                                                </div>
                                            </div>
                                            
                                            <div className="d-flex justify-content-end">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleSave}
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FontAwesomeIcon icon={faSave} className="me-2" />
                                                            Save Changes
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>

                                {/* Security Settings */}
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white border-0">
                                        <h5 className="card-title mb-0">
                                            <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                                            Security Settings
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    <div>
                                                        <h6 className="mb-1">Two-Factor Authentication</h6>
                                                        <p className="text-muted small mb-0">Add an extra layer of security to your account</p>
                                                    </div>
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="security.twoFactor"
                                                            checked={settings.security.twoFactor}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="row mb-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Session Timeout (minutes)</label>
                                                <select
                                                    className="form-select"
                                                    name="security.sessionTimeout"
                                                    value={settings.security.sessionTimeout}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value={15}>15 minutes</option>
                                                    <option value={30}>30 minutes</option>
                                                    <option value={60}>1 hour</option>
                                                    <option value={120}>2 hours</option>
                                                    <option value={480}>8 hours</option>
                                                </select>
                                            </div>
                                        </div>
                                        
                                        <div className="d-flex justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary"
                                                onClick={changePassword}
                                            >
                                                <FontAwesomeIcon icon={faKey} className="me-2" />
                                                Change Password
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar */}
                            <div className="col-lg-4">
                                {/* Notification Settings */}
                                <div className="card border-0 shadow-sm mb-4">
                                    <div className="card-header bg-white border-0">
                                        <h6 className="card-title mb-0">
                                            <FontAwesomeIcon icon={faBell} className="me-2" />
                                            Notification Preferences
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="notifications.email"
                                                    checked={settings.notifications.email}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label">
                                                    Email Notifications
                                                </label>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="notifications.sms"
                                                    checked={settings.notifications.sms}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label">
                                                    SMS Notifications
                                                </label>
                                            </div>
                                        </div>
                                        <div className="mb-0">
                                            <div className="form-check">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="notifications.push"
                                                    checked={settings.notifications.push}
                                                    onChange={handleInputChange}
                                                />
                                                <label className="form-check-label">
                                                    Push Notifications
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Help */}
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <h6 className="card-title mb-0">
                                            <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                                            Quick Help
                                        </h6>
                                    </div>
                                    <div className="card-body">
                                        <ul className="list-unstyled small">
                                            <li className="mb-2">
                                                <a href="#" className="text-decoration-none">How to change password</a>
                                            </li>
                                            <li className="mb-2">
                                                <a href="#" className="text-decoration-none">Enable two-factor authentication</a>
                                            </li>
                                            <li className="mb-2">
                                                <a href="#" className="text-decoration-none">Manage notification preferences</a>
                                            </li>
                                            <li className="mb-2">
                                                <a href="#" className="text-decoration-none">Account security best practices</a>
                                            </li>
                                            <li className="mb-0">
                                                <a href="#" className="text-decoration-none">Contact support</a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default AccountHelp;
